import { Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { PreloadedPages, PreloadedPagesByDocument } from "src/app/models/document.model";
import { DomSanitizer } from "@angular/platform-browser";
import { PagesApiClient } from "src/app/services/api-clients/pages.api-client";
import { SingleOrDefault } from "../_helpers/ArrayExtensionMethodHelper.functions";

@Injectable({ providedIn: 'root' })
export class DocumentPagesService {

    private _alreadyLoadedPagesByDocument: PreloadedPagesByDocument[] = [];

    constructor(private sanitizer: DomSanitizer,
        private pageService: PagesApiClient) { }

    public async getAlreadyLoadedPages(requestGuid: string, documentGuid: string): Promise<PreloadedPagesByDocument> {
        // existing?
        let documentPagesModel = SingleOrDefault(this._alreadyLoadedPagesByDocument, "documentId", documentGuid);
        if (documentPagesModel) {
            return documentPagesModel;
        }
        // instantiate the FIRST page and return:
        await this.requestPage(requestGuid, documentGuid, 1);
        return SingleOrDefault(this._alreadyLoadedPagesByDocument, "documentId", documentGuid)!;
    }

    public async resetAlreadyLoadedPagesAndReLoadThem(requestGuid: string, documentGuid: string) {
        // existing?
        let documentPagesIndex = this._alreadyLoadedPagesByDocument.findIndex(s => s.documentId === documentGuid);
        if(documentPagesIndex > -1) {
            this._alreadyLoadedPagesByDocument.splice(documentPagesIndex,1);
        }
        this.getAlreadyLoadedPages(requestGuid, documentGuid);
    }

    private _pendingPageRequests = new Map<string, Promise<PreloadedPagesByDocument>>();

    public async requestPage(requestGuid: string, documentGuid: string, pageId: number): Promise<PreloadedPagesByDocument> {
        let documentPagesModel = SingleOrDefault(this._alreadyLoadedPagesByDocument, "documentId", documentGuid);
        if (documentPagesModel) {
            let existingPage = SingleOrDefault(documentPagesModel.preloadedPages, "pageId", pageId);
            if (existingPage) {
                return documentPagesModel;
            }
        }

        let requestKey = `${documentGuid}_${pageId}`;
        let pendingRequest = this._pendingPageRequests.get(requestKey);
        if (pendingRequest) {
            return pendingRequest;
        }

        let requestPromise = this.fetchPage(requestGuid, documentGuid, pageId);
        this._pendingPageRequests.set(requestKey, requestPromise);
        try {
            return await requestPromise;
        } finally {
            this._pendingPageRequests.delete(requestKey);
        }
    }

    private async fetchPage(requestGuid: string, documentGuid: string, pageId: number): Promise<PreloadedPagesByDocument> {
        let getPage = await this.pageService.getPage(requestGuid, documentGuid, pageId);
        let page = await lastValueFrom(getPage);
        let pageImage = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(page));

        // deze check + eventuele create+push gebeurt volledig synchroon (geen await ertussen),
        // dus is atomisch t.o.v. andere gelijktijdige aanroepen voor hetzelfde/een ander document.
        let documentPagesModel = SingleOrDefault(this._alreadyLoadedPagesByDocument, "documentId", documentGuid);
        if (!documentPagesModel) {
            documentPagesModel = new PreloadedPagesByDocument();
            documentPagesModel.documentId = documentGuid;
            documentPagesModel.preloadedPages = [];
            this._alreadyLoadedPagesByDocument.push(documentPagesModel);
        }
        let preloadedPage: PreloadedPages = {
            pageId: pageId,
            pageUrl: pageImage
        }
        documentPagesModel.preloadedPages.push(preloadedPage);
        return documentPagesModel;
    }
}
